import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Plus, X, Check, User, Link, Building, Mail } from 'lucide-react';
import confetti from 'canvas-confetti';
import { apiService } from '../services/api';
import { TabConfig } from '../hooks/useTableTabs';
import './AddFounder.css';

interface AddFounderProps {
  onFounderAdded?: (founderId?: string) => void;
  buttonClassName?: string;
  activeTab?: TabConfig;
}

interface FounderData {
  name: string;
  profileUrl?: string;
  company?: string;
  roleTitle?: string;
  industry?: string;
  location?: string;
  email?: string;
  followerCount?: number;
}

const AddFounder: React.FC<AddFounderProps> = ({ onFounderAdded, buttonClassName, activeTab }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [isPolling, setIsPolling] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [formData, setFormData] = useState<FounderData>({
    name: '',
    profileUrl: '',
    company: '',
    roleTitle: '',
    industry: '',
    location: '',
    email: '',
    followerCount: undefined
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'followerCount' ? (value ? parseInt(value) : undefined) : value
    }));
  };

  const triggerConfetti = () => {
    const end = Date.now() + 3 * 1000; // 3 seconds
    const colors = ['#ff3951', '#ffe845', '#aaff00', '#00ffec']; // Red, Yellow, Green, Cyan

    const frame = () => {
      if (Date.now() > end) return;

      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: colors,
        zIndex: 10001,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        colors: colors,
        zIndex: 10001,
      });

      requestAnimationFrame(frame);
    };

    frame();
  };

  const pollJobStatus = async () => {
    try {
      const status = await apiService.getJobQueueStatus();
      setJobStatus(`Queue: ${status.pending} pending, ${status.running} running`);
      
      // If no jobs are running or pending, the job is complete
      if (status.running === 0 && status.pending === 0) {
        setIsPolling(false);
        setJobStatus('Job completed! Closing in 10 seconds...');
        setCountdown(10);
        
        // Start countdown
        const countdownInterval = setInterval(() => {
          setCountdown(prev => {
            if (prev === null || prev <= 1) {
              clearInterval(countdownInterval);
              handleClose();
              onFounderAdded?.();
              return null;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      console.error('Error polling job status:', error);
      setIsPolling(false);
    }
  };
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Only pass tabId if user is in a specific tab (not "All People" or default)
      // If no tabId is passed, founder will only be added to "All People" tab
      const tabId = activeTab?.name && activeTab.name !== 'All People' && activeTab.id !== 'default' 
        ? activeTab.name 
        : undefined;

      // Send profileUrl only if it's not empty
      const founderData: any = {
        ...formData,
        profileUrl: formData.profileUrl?.trim() || undefined,
        tabId: tabId
      };
      const newFounder = await apiService.createFoundername(founderData);
      console.log('Founder added successfully:', newFounder);
      
      // Call onFounderAdded with the new founder ID
      onFounderAdded?.(newFounder?.id);
      
      setSuccess(true);
      triggerConfetti(); // Trigger confetti on success!
      setJobStatus('Job queued, checking status...');
      setIsPolling(true);
      
      // Start polling for job completion
      const pollInterval = setInterval(() => {
        if (!isPolling) {
          clearInterval(pollInterval);
          return;
        }
        pollJobStatus();
      }, 2000); // Poll every 2 seconds
      
      // Stop polling after 5 minutes as fallback
      setTimeout(() => {
        if (isPolling) {
          setIsPolling(false);
          clearInterval(pollInterval);
          setJobStatus('Job taking longer than expected. Please refresh manually.');
        }
      }, 300000); // 5 minutes
      
      // Don't close the modal automatically - let user close it manually

    } catch (err: any) {
      // Check if it's a "founder already exists" error (409)
      const is409Error = err?.status === 409 || err?.response?.status === 409;
      const messageIncludesExists = err?.message && (
        err.message.toLowerCase().includes('already exists') ||
        err.message.toLowerCase().includes('founder already exists')
      );
      
      if (is409Error || messageIncludesExists) {
        // Founder exists - try to find and add them to the active tab
        try {
          // Search for the founder by name - try multiple pages
          let foundFounder = null;
          let page = 1;
          const limit = 50; // Use a smaller limit to avoid API errors
          const searchName = formData.name.toLowerCase().trim();
          const searchWords = searchName.split(/\s+/).filter(w => w.length > 0);
          
          console.log('Searching for existing founder:', formData.name, 'searchName:', searchName, 'words:', searchWords);
          
          // Search up to 20 pages (1000 founders)
          while (page <= 20 && !foundFounder) {
            try {
              const response = await apiService.getFounders(limit, page);
              const founders = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
              
              console.log(`Page ${page}: Found ${founders.length} founders`);
              
              if (founders.length === 0) {
                console.log('No founders on this page, stopping search');
                break;
              }
              
              // Try exact match first
              foundFounder = founders.find((f: any) => {
                if (!f.name) return false;
                const founderName = f.name.toLowerCase().trim();
                return founderName === searchName;
              });
              
              // If not found, try matching all words in the search name
              if (!foundFounder && searchWords.length > 0) {
                foundFounder = founders.find((f: any) => {
                  if (!f.name) return false;
                  const founderName = f.name.toLowerCase().trim();
                  // Check if all words in search name are in founder name
                  const allWordsMatch = searchWords.every(word => founderName.includes(word));
                  // Also check if founder name contains the search name (for partial matches)
                  return allWordsMatch || founderName.includes(searchName) || searchName.includes(founderName);
                });
              }
              
              // If found, break
              if (foundFounder) {
                console.log('Found existing founder:', foundFounder.name, foundFounder.id);
                break;
              }
              
              // Check if there are more pages
              if (response.pagination) {
                if (page >= response.pagination.pages) {
                  console.log('No more pages to search (reached last page)');
                  break;
                }
                console.log(`Page ${page}/${response.pagination.pages}, continuing search...`);
              } else if (founders.length < limit) {
                // If we got fewer results than the limit, we've reached the end
                console.log('Got fewer results than limit, stopping search');
                break;
              }
            } catch (pageErr) {
              console.error(`Error fetching page ${page}:`, pageErr);
              // If we get an error (like 400), stop searching
              break;
            }
            page++;
          }
          
          if (foundFounder) {
            // Found the existing founder - add them to the active tab
            onFounderAdded?.(foundFounder.id);
            setSuccess(true);
            setError(null);
            triggerConfetti();
            setJobStatus(`${formData.name} already exists and has been added to this table`);
            return;
          } else {
            console.log('Searched through all pages but founder not found:', formData.name);
            // Show a more helpful error message
            setError(`${formData.name} already exists in the database, but we couldn't find them in the search. They may need to be added manually to this table.`);
          }
        } catch (searchErr) {
          console.error('Error searching for existing founder:', searchErr);
          setError(`${formData.name} already exists, but we encountered an error searching for them: ${searchErr instanceof Error ? searchErr.message : 'Unknown error'}`);
        }
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setError(null);
    setSuccess(false);
    setIsPolling(false);
    setJobStatus('');
    setCountdown(null);
    setFormData({
      name: '',
      profileUrl: '',
      company: '',
      roleTitle: '',
      industry: '',
      location: '',
      email: '',
      followerCount: undefined
    });
    
    // Only refresh if success was already shown (founder was added)
    // Otherwise, refresh when closing to ensure data is up to date
    if (!success) {
      onFounderAdded?.();
    }
  };

  if (!isOpen) {
    return (
      <button 
        className={buttonClassName || "add-founder-button"}
        onClick={() => setIsOpen(true)}
        title="Add New Person"
      >
        <Plus size={18} />
      </button>
    );
  }

  const modalContent = (
    <div className="add-founder-overlay">
      <div className="add-founder-modal">
        <div className="add-founder-header">
          <h2>Add New Person</h2>
          <button 
            className="close-button"
            onClick={handleClose}
            style={{ cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {success && (
          <div className="success-message">
            <Check size={16} />
            <div>
              <div>We're fetching their LinkedIn updates and tracker will update once done</div>
              {jobStatus && (
                <div style={{ fontSize: '0.9em', marginTop: '4px', opacity: 0.8 }}>
                  {jobStatus}
                  {countdown !== null && (
                    <div style={{ fontWeight: 'bold', color: '#4ade80', marginTop: '2px' }}>
                      Auto-closing in {countdown} seconds...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="add-founder-form">
          <div className="form-group">
            <div className="input-with-icon">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Name *"
              />
              {!formData.name && <User className="field-icon" size={16} />}
            </div>
          </div>

          <div className="form-group">
            <div className="input-with-icon">
              <input
                type="url"
                id="profileUrl"
                name="profileUrl"
                value={formData.profileUrl}
                onChange={handleInputChange}
                placeholder="LinkedIn URL"
              />
              {!formData.profileUrl && <Link className="field-icon" size={16} />}
            </div>
          </div>

          <div className="form-group">
            <div className="input-with-icon">
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                placeholder="Company"
              />
              {!formData.company && <Building className="field-icon" size={16} />}
            </div>
          </div>

          <div className="form-group">
            <div className="input-with-icon">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email"
              />
              {!formData.email && <Mail className="field-icon" size={16} />}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="add-founder-submit-button"
              disabled={loading || success || !formData.name}
            >
              {loading ? 'Syncing information, this might take a minute...' : 
               success ? 'Processing...' : 'Add Person'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AddFounder;
